import React from 'react';

import type { IAjuste } from '../../../../../../hooks/useFechamentoContabilMensal';
import { num, TIPO_DE_AJUSTE } from '../../regrasDoDetalhe';
import { dataEHora } from '../../regrasDoFechamento';
import { CABECA_DA_TABELA, CAIXA, pilula, SUB, TITULO } from './estilos';

const conta = (codigo: string | null, nome: string | null) =>
  codigo ? `${codigo} · ${nome ?? ''}`.replace(/ · $/, '') : '—';

const CONFIANCA: Record<string, [string, string, string]> = {
  alta: ['Alta', '#DCFCE7', '#15803D'],
  media: ['Média', '#FEF3C7', '#B45309'],
};

const corDoEfeito = (efeito: number | null) => {
  if (!efeito) return '#52525B';
  return efeito < 0 ? '#B91C1C' : '#15803D';
};

/**
 * Aba Ajustes propostos do detalhe. O que entra no arquivo é decidido pelo sistema
 * (decisão de 16/10/2026): todo ajuste proposto vai, menos o que o Domínio não
 * importaria. Por isso a marcação da lista só mostra a situação, não se muda à mão.
 * Só leitura: a geração do arquivo chega com o fechamento pela tela.
 */
export function AbaAjustes({
  ajustes,
  lucroDoMes,
}: {
  ajustes: IAjuste[];
  lucroDoMes: number | null | undefined;
}) {
  const fora = ajustes.filter(a => a.motivo_fora).length;
  const entram = ajustes.length - fora;
  const efeito = ajustes
    .filter(a => !a.motivo_fora)
    .reduce((soma, a) => soma + (a.efeito_lucro ?? 0), 0);

  const resumo = [
    {
      nome: 'Ajustes propostos',
      valor: String(ajustes.length),
      sub: `${entram} ${entram === 1 ? 'entra' : 'entram'} no arquivo · ${fora} ${
        fora === 1 ? 'fica' : 'ficam'
      } fora`,
      cor: '#0F172A',
    },
    {
      nome: 'Efeito no lucro do mês',
      valor: num(efeito),
      sub:
        lucroDoMes == null
          ? 'dos ajustes que entram no arquivo'
          : `dos ajustes que entram no arquivo · lucro iria de ${num(lucroDoMes)} para ${num(
              lucroDoMes + efeito,
            )}`,
      cor: efeito < 0 ? '#B91C1C' : '#0F172A',
    },
  ];

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 12,
        }}
      >
        {resumo.map(r => (
          <div key={r.nome} style={{ ...CAIXA, gap: 4 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: '#52525B' }}>{r.nome}</span>
            <b style={{ fontSize: 22, color: r.cor }}>{r.valor}</b>
            <span style={SUB}>{r.sub}</span>
          </div>
        ))}
      </div>

      <div style={{ ...CAIXA, gap: 8 }}>
        <div style={TITULO}>Lançamentos sugeridos pelo motor</div>
        <span style={SUB}>
          Proposta do sistema a partir das constatações. Nada foi gravado no Domínio; o que chega lá
          é o arquivo que a pessoa importa. Confiança alta: valor e contas vêm do dado; média: a
          conta de destino foi inferida e precisa de conferência.
        </span>
        {ajustes.length === 0 ? (
          <p
            style={{
              margin: 0,
              padding: 20,
              textAlign: 'center',
              fontSize: 12.5,
              color: '#52525B',
            }}
          >
            Nenhum ajuste proposto para esta competência.
          </p>
        ) : (
          <div
            className="czScroll"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            role="region"
            aria-label="Lista de ajustes propostos"
            style={{ overflowX: 'auto' }}
          >
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 940 }}
            >
              <thead>
                <tr style={CABECA_DA_TABELA}>
                  <th style={{ padding: '6px 8px', width: 28 }} aria-label="No arquivo" />
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Origem</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Tipo</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Débito</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Crédito</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>Valor</th>
                  <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'right' }}>
                    Efeito no lucro
                  </th>
                  <th style={{ padding: '6px 8px', fontWeight: 600 }}>Confiança</th>
                </tr>
              </thead>
              {ajustes.map(a => {
                const conf = CONFIANCA[a.confianca] ?? CONFIANCA.media;
                const entra = !a.motivo_fora;
                return (
                  <tbody key={a.id}>
                    <tr style={{ borderTop: '1px solid #EEF2F6', verticalAlign: 'top' }}>
                      <td style={{ padding: 8 }}>
                        <input
                          type="checkbox"
                          checked={entra}
                          readOnly
                          disabled
                          aria-label={`${a.codigo_achado ?? 'Ajuste'} ${
                            entra ? 'entra' : 'não entra'
                          } no arquivo`}
                          title={
                            entra
                              ? 'Entra no arquivo do Domínio'
                              : 'Fica fora: o Domínio não importaria'
                          }
                          style={{ width: 15, height: 15, accentColor: '#1B6FD0' }}
                        />
                      </td>
                      <td style={{ padding: 8, fontWeight: 700, color: '#1B6FD0' }}>
                        {a.codigo_achado ?? '—'}
                      </td>
                      <td style={{ padding: 8 }}>{TIPO_DE_AJUSTE[a.tipo] ?? a.tipo}</td>
                      <td style={{ padding: 8 }}>{conta(a.conta_debito, a.nome_debito)}</td>
                      <td style={{ padding: 8 }}>{conta(a.conta_credito, a.nome_credito)}</td>
                      <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>
                        {num(a.valor)}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          textAlign: 'right',
                          color: corDoEfeito(a.efeito_lucro),
                        }}
                      >
                        {a.efeito_lucro ? num(a.efeito_lucro) : '—'}
                      </td>
                      <td style={{ padding: 8 }}>
                        <span style={pilula(conf[1], conf[2])}>{conf[0]}</span>
                      </td>
                    </tr>
                    <tr>
                      <td aria-hidden="true" />
                      <td
                        colSpan={7}
                        style={{ padding: '0 8px 10px', fontSize: 11.5, color: '#52525B' }}
                      >
                        {a.historico}
                        {a.base_legal && ` · ${a.base_legal}`}
                        {a.motivo_fora && (
                          <span
                            style={{
                              display: 'block',
                              marginTop: 4,
                              color: '#B45309',
                              fontWeight: 600,
                            }}
                          >
                            Fica fora do arquivo: {a.motivo_fora}
                          </span>
                        )}
                        {!a.motivo_fora && a.exportado_em && (
                          <span style={{ display: 'block', marginTop: 4, color: '#155FB0' }}>
                            No arquivo de {dataEHora(a.exportado_em)}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </div>
    </>
  );
}
